import { useRef, useEffect } from 'react'
import { gsap } from 'gsap'

interface ThrottleGaugeProps {
  size?: number // width in px
  enableAnimation?: boolean // toggle animation
  staticPosition?: number // 0 → released, 1 → fully pressed
}

export default function ThrottleGauge({
  size = 200,
  enableAnimation = true,
  staticPosition = 0.5,
}: ThrottleGaugeProps) {
  const progressRef = useRef<SVGPathElement>(null)
  const tlRef = useRef<gsap.core.Tween | null>(null)

  useEffect(() => {
    const el = progressRef.current
    if (!el) return

    const length = el.getTotalLength()
    gsap.set(el, {
      strokeDasharray: length,
      strokeDashoffset: 0,
    })

    // 🔴 Kill previous timeline if toggling props
    if (tlRef.current) {
      tlRef.current.kill()
      tlRef.current = null
    }

    if (!enableAnimation) {
      // just set static position (freeze)
      gsap.set(el, {
        strokeDashoffset: -(length * staticPosition),
      })
      return
    }

    const levels = [length * 0.25, length * 0.5, length * 0.75, length]

    function animateThrottle() {
      const next = gsap.utils.random(levels)
      const pressDuration = gsap.utils.random(1.6, 2.2)
      const hold = gsap.utils.random(0.3, 0.8)
      const releaseDuration = gsap.utils.random(2.2, 2.6)

      tlRef.current = gsap.to(el, {
        strokeDashoffset: -next,
        duration: pressDuration,
        ease: 'power2.out',
        onComplete: () => {
          gsap.delayedCall(hold, () => {
            tlRef.current = gsap.to(el, {
              strokeDashoffset: 0,
              duration: releaseDuration,
              ease: 'power2.inOut',
              onComplete: animateThrottle,
            })
          })
        },
      })
    }

    animateThrottle()

    // cleanup
    return () => {
      if (tlRef.current) tlRef.current.kill()
    }
  }, [enableAnimation, staticPosition])

  const baseWidth = 200
  const baseHeight = 100
  const scale = size / baseWidth

  return (
    <svg
      width={size}
      height={baseHeight * scale}
      viewBox="0 0 200 100"
      style={{ display: 'block' }}
    >
      {/* background tube */}
      <path fill="transparent" strokeWidth={60} stroke="white" d="M30 100 A 40 40 0 0 1 170 100" />

      {/* throttle progress */}
      <path
        ref={progressRef}
        fill="transparent"
        strokeWidth={60}
        stroke="gold"
        d="M30 100 A 40 40 0 0 1 170 100"
      />
    </svg>
  )
}
